import { forwardRef, useState } from "react";
import { useSnackbar } from "notistack";
import _ from "lodash";
import moment, { Moment } from "moment";
import { NumericFormat, NumericFormatProps } from "react-number-format";

import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import CloseIcon from "@mui/icons-material/Close";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

import { CustomDialog } from "../shared/customDialog";
import { useFileUpload } from "../../hooks/useFileUpload";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { setPortalProps } from "../../store/modules/portalSlice";
import { gray, primary, error } from "../../theme/colors";
import * as api from "../../api";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_TYPES = ["application/pdf", "image/png", "image/jpeg"];

interface NumericFormatCustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const NumericFormatCustom = forwardRef<
  NumericFormatProps,
  NumericFormatCustomProps
>(function NumericFormatCustom(props, ref) {
  const { onChange, ...other } = props;
  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({ target: { name: props.name, value: values.value } });
      }}
      thousandSeparator=","
      valueIsNumericString
      allowNegative={false}
      decimalScale={0}
    />
  );
});

interface CoiDocumentUploadDialogProps {
  open: boolean;
  onClose: () => void;
  bannerKey: string;
  bannerData: Record<string, any>;
}

export const CoiDocumentUploadDialog = ({
  open,
  onClose,
  bannerKey,
  bannerData,
}: CoiDocumentUploadDialogProps) => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { id, banners } = useAppSelector(
    (state) => state.portal || ({} as any)
  );
  const { upload, uploading } = useFileUpload();

  const [amount, setAmount] = useState("");
  const [expiration, setExpiration] = useState<Moment | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const amountValue = amount.trim();

  const reset = () => {
    setAmount("");
    setExpiration(null);
    setFile(null);
    setFileError(null);
    setIsDragging(false);
  };

  const handleClose = () => {
    if (submitting || uploading) return;
    reset();
    onClose();
  };

  const validateFile = (f: File): string | null => {
    if (!ACCEPTED_TYPES.includes(f.type)) return "PDF or image files only";
    if (f.size > MAX_FILE_SIZE) return "File exceeds 10 MB";
    return null;
  };

  const handleFile = (f: File) => {
    const err = validateFile(f);
    if (err) {
      setFileError(err);
      setFile(null);
      return;
    }
    setFileError(null);
    setFile(f);
  };

  const isValid =
    !!amountValue &&
    !!expiration &&
    expiration.isValid() &&
    !!file &&
    !fileError;

  const handleSubmit = async () => {
    if (!isValid || !file) return;
    setSubmitting(true);
    try {
      const uploadRes = await upload({
        portalId: id,
        file,
        partialPayload: bannerData.partialUploadPayload,
      });

      const updatePayload: any = {
        fields: {
          coi_coverage_amount: parseInt(amountValue),
          coi_expiration_date: expiration!.startOf("day").valueOf(),
        },
        sendCoiEmailNotification: {
          systemSize: bannerData.systemSize.toString(),
          region: bannerData.region,
          fileUrl: `${process.env.REACT_APP_SUNRISE_PREVIEW_FILES_URL}/${uploadRes.id}/${uploadRes.name}`,
          fileName: uploadRes.name,
          taskId: bannerData.taskId,
          coverageAmount: `$ ${Number(amountValue).toLocaleString("en-US")}`,
          policyExpiration: expiration!.format("MM/DD/YYYY"),
        }
      } as any;

      await api.portal.update(id, updatePayload);

      const newBanners = _.map(banners, (b: any) =>
        b.bannerKey === bannerKey ? { ...b, status: "in_progress" } : b
      );
      dispatch(setPortalProps({ banners: newBanners }));

      enqueueSnackbar("Certificate of Insurance submitted", {
        variant: "success",
      });
      reset();
      onClose();
    } catch (err: any) {
      console.error(err);
      enqueueSnackbar(err?.message || "Failed to submit COI. Please try again.", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const disableActions = submitting || uploading;

  return (
    <CustomDialog
      open={open}
      handleClose={handleClose}
      title="Upload your Certificate of Insurance"
      content={
        <Stack gap={5}>
          <Typography variant="body2" color={gray[700]}>
            Submitting your COI early helps your project move through utility
            interconnection approval about 2× faster.
          </Typography>

          <Stack gap={2}>
            <Typography
              variant="body2"
              fontWeight={500}
              color={gray[800]}
              fontSize="14px"
            >
              Policy coverage amount
            </Typography>
            <FormControl variant="outlined" fullWidth>
              <TextField
                name="amount"
                placeholder="Enter coverage amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={disableActions}
                InputProps={{
                  inputComponent: NumericFormatCustom as any,
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </FormControl>
          </Stack>

          <Stack gap={2}>
            <Typography
              variant="body2"
              fontWeight={500}
              color={gray[800]}
              fontSize="14px"
            >
              Policy expiration date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                value={expiration}
                onChange={(v) => setExpiration(v)}
                minDate={moment()}
                disabled={disableActions}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: "mm / dd / yyyy",
                  },
                }}
              />
            </LocalizationProvider>
          </Stack>

          <Stack gap={2}>
            <Typography
              variant="body2"
              fontWeight={500}
              color={gray[800]}
              fontSize="14px"
            >
              Certificate of Insurance file
            </Typography>
            {!file ? (
              <Stack
                component="label"
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  const dropped = e.dataTransfer.files?.[0];
                  if (dropped) handleFile(dropped);
                }}
                sx={{
                  backgroundColor: isDragging ? primary[25] : gray[25],
                  border: "1.5px dashed",
                  borderColor: fileError
                    ? error[500]
                    : isDragging
                    ? primary[400]
                    : gray[100],
                  padding: "24px 16px",
                  borderRadius: "8px",
                  gap: 2,
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: disableActions ? "not-allowed" : "pointer",
                  opacity: disableActions ? 0.6 : 1,
                }}
              >
                <Stack direction="row" alignItems="center" gap={2}>
                  <FileUploadRoundedIcon
                    sx={{ color: gray[800], width: 20, height: 20 }}
                  />
                  <Typography
                    variant="subtitle2"
                    color={gray[800]}
                    fontWeight={500}
                  >
                    Drag & drop or browse
                  </Typography>
                </Stack>
                <Typography variant="body2" color={gray[500]}>
                  PDF or image · up to 10 MB
                </Typography>
                <input
                  type="file"
                  hidden
                  disabled={disableActions}
                  accept=".pdf,image/png,image/jpeg"
                  onChange={(e) => {
                    const picked = e.target.files?.[0];
                    if (picked) handleFile(picked);
                  }}
                />
              </Stack>
            ) : (
              <Stack
                sx={{
                  padding: "12px 16px",
                  border: "1px solid",
                  borderColor: fileError ? error[500] : gray[300],
                  borderRadius: "8px",
                }}
                direction="row"
                alignItems="center"
                gap={3}
              >
                <InsertDriveFileRoundedIcon sx={{ color: primary[400] }} />
                <Box flex="auto" minWidth={0}>
                  <Typography
                    variant="subtitle2"
                    color={gray[800]}
                    fontWeight={500}
                    noWrap
                  >
                    {file.name}
                  </Typography>
                  <Typography variant="body2" color={gray[500]}>
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => {
                    setFile(null);
                    setFileError(null);
                  }}
                  disabled={disableActions}
                  aria-label="remove file"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
            {fileError && (
              <Typography variant="body2" color={error[600]}>
                {fileError}
              </Typography>
            )}
          </Stack>
        </Stack>
      }
      lCta={
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={disableActions}
          sx={{
            backgroundColor: "#ffffff",
            borderColor: gray[100],
            color: gray[700],
            fontWeight: 500,
            fontSize: "15px",
            textTransform: "none",
            borderRadius: "8px",
            padding: "13px 22px",
            "&:hover": {
              backgroundColor: gray[25],
              borderColor: gray[200],
            },
          }}
        >
          Cancel
        </Button>
      }
      rCta={
        <Button
          variant="contained"
          disabled={!isValid || disableActions}
          onClick={handleSubmit}
          sx={{
            backgroundColor: primary[400],
            color: "#ffffff",
            fontWeight: 500,
            fontSize: "15px",
            textTransform: "none",
            borderRadius: "8px",
            padding: "13px 22px",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: primary[500],
              boxShadow: "none",
            },
          }}
        >
          {disableActions && (
            <CircularProgress
              color="inherit"
              size={16}
              thickness={4.5}
              sx={{ mr: 1 }}
            />
          )}
          Submit COI
        </Button>
      }
    />
  );
};
